import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  useRef,
} from "react";
import { Checkbox, FlashMessage, Input, SelectNew } from "../../components/common";
import { Box, Button, FormControlLabel, InputAdornment, Radio, RadioGroup, Typography } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Slider from "@material-ui/core/Slider";
import JobSidebarLoader from "./JobSidebarLoader";
import currenciesServices from "../../services/currencies.services";

type LocationOption = "All" | "On Site" | "Remote" | "Hybrid";
type JobTypeOption =
  | "All"
  | "Full Time"
  | "Part Time"
  | "Contract"
  | "Internship";
type ExperienceOption =
  | "All"
  | "Entry Level"
  | "Intermediate"
  | "Senior"
  | "Expert";
type RelevantOption = "All" | "Week" | "Month";
type SalaryPeriodOption = "Yearly" | "Hourly";

export interface Filters {
  relevant: RelevantOption;
  locations: LocationOption[];
  jobType: JobTypeOption[];
  experience: ExperienceOption[];
  salaryRange: [number, number];
  /** 0 = all currencies (no API filter); otherwise currencies.id */
  currency: number;
  salaryTerm: "annual" | "per_hour";
  searchLocation: string;
}

interface JobSidebarProps {
  onFilterChange: (filters: Partial<Filters>) => void;
  initialFilters?: Filters;
  jobData?: any;
  setFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filterOpen: boolean;
}

const RELEVANT_OPTIONS: readonly RelevantOption[] = ["All", "Week", "Month"];
const LOCATION_OPTIONS: readonly LocationOption[] = [
  "All",
  "On Site",
  "Remote",
  "Hybrid",
];
const JOB_TYPE_OPTIONS: readonly JobTypeOption[] = [
  "All",
  "Full Time",
  "Part Time",
  "Contract",
  "Internship",
];
const EXPERIENCE_OPTIONS: readonly ExperienceOption[] = [
  "All",
  "Entry Level",
  "Intermediate",
  "Senior",
  "Expert",
];

const ANNUAL_SALARY_SLIDER_MAX = 3000;
const HOURLY_RATE_SLIDER_MAX = 500;
/** Fallback id until /currencies loads; synced to real USD row when API returns */
const DEFAULT_CURRENCY_USD_ID = 1;
const ALL_CURRENCIES_ID = 0;
const USD_SALARY_SYMBOL = "$";
const SALARY_PERIOD_OPTIONS: readonly SalaryPeriodOption[] = ["Yearly", "Hourly"];
const SALARY_PERIOD_SELECT_OPTIONS = SALARY_PERIOD_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));
interface CurrencyOption {
  value: string;
  label: string;
  symbol: string;
  code: string;
}

const defaultFilters: Filters = {
  relevant: "All",
  locations: ["All"],
  jobType: ["All"],
  experience: ["All"],
  salaryRange: [0, ANNUAL_SALARY_SLIDER_MAX],
  currency: DEFAULT_CURRENCY_USD_ID,
  salaryTerm: "annual",
  searchLocation: "",
};

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

function withAllLogic<T extends string>(
  selected: T[],
  clicked: T,
  single = false
): T[] {
  const ALL = "All" as T;

  if (clicked === ALL) return [ALL];

  if (single) {
    return [clicked];
  }

  const cleaned = selected.filter((v) => v !== ALL);

  const nextSelected = cleaned.includes(clicked)
    ? cleaned.filter((v) => v !== clicked)
    : [...cleaned, clicked];

  return nextSelected.length === 0 ? [ALL] : nextSelected;
}

interface FilterGroupProps<T extends string> {
  title?: string;
  options: readonly T[];
  selected: readonly T[];
  mode?: "single" | "multi";
  columns?: 1 | 2;
  embedded?: boolean;
  onChange: (next: T[]) => void;
  formatOptionLabel?: (option: T) => string;
}

const FilterGroup = React.memo(function FilterGroupInner<T extends string>({
  title,
  options,
  selected,
  mode = "multi",
  columns = 1,
  embedded = false,
  onChange,
  formatOptionLabel,
}: FilterGroupProps<T>) {
  const id = useId();
  const groupName = title ? `${title}-${id}` : id;
  const selectedValue = (selected[0] ?? "") as T;

  const handleClick = useCallback(
    (option: T) => {
      const next = withAllLogic(selected as T[], option, mode === "single");
      onChange(next);
    },
    [selected, mode, onChange]
  );

  const optionLabel = (option: T) =>
    formatOptionLabel ? formatOptionLabel(option) : option;

  const optionsMarkup =
    mode === "single" ? (
      <RadioGroup
        className={`search-radios${columns === 2 ? " search-radios--two-col" : ""}`}
        aria-labelledby={title ? id : undefined}
        name={groupName}
        value={selectedValue}
        onChange={(event) => handleClick(event.target.value as T)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio color="primary" />}
            label={optionLabel(option)}
          />
        ))}
      </RadioGroup>
    ) : (
      <div className="search-checkboxes">
        {options.map((option) => (
          <Checkbox
            key={option}
            externalLabel={{ label: optionLabel(option) }}
            checked={selected.includes(option)}
            onClick={() => handleClick(option)}
          />
        ))}
      </div>
    );

  if (embedded) {
    return optionsMarkup;
  }

  return (
    <div className="search-option">
      {title && <h5 id={id}>{title}</h5>}
      {optionsMarkup}
    </div>
  );
}) as <T extends string>(p: FilterGroupProps<T>) => JSX.Element;

const JobSidebar: React.FC<JobSidebarProps> = ({
  onFilterChange,
  setFilterOpen,
  filterOpen,
}) => {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const initial = useRef(true);
  const [loading, setLoading] = useState(true);

  const [relevant, setRelevant] = useState<RelevantOption>(
    defaultFilters.relevant
  );
  const [locations, setLocations] = useState<LocationOption[]>(
    defaultFilters.locations
  );
  const [jobType, setJobType] = useState<JobTypeOption[]>(
    defaultFilters.jobType
  );
  const [experience, setExperience] = useState<ExperienceOption[]>(
    defaultFilters.experience
  );
  const [range, setRange] = useState<[number, number]>(
    defaultFilters.salaryRange
  );
  const [salaryPeriod, setSalaryPeriod] = useState<SalaryPeriodOption>("Yearly");
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([
    {
      value: String(ALL_CURRENCIES_ID),
      label: "All currencies",
      symbol: USD_SALARY_SYMBOL,
      code: "ALL",
    },
    {
      value: String(DEFAULT_CURRENCY_USD_ID),
      label: "USD",
      symbol: USD_SALARY_SYMBOL,
      code: "USD",
    },
  ]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>(
    String(DEFAULT_CURRENCY_USD_ID)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const currentSalarySliderMax =
    salaryPeriod === "Hourly"
      ? HOURLY_RATE_SLIDER_MAX
      : ANNUAL_SALARY_SLIDER_MAX;
  const currentSalarySliderStep = salaryPeriod === "Hourly" ? 5 : 10;
  const selectedCurrency = useMemo(
    () =>
      currencyOptions.find((currency) => currency.value === selectedCurrencyId) ??
      currencyOptions[0],
    [currencyOptions, selectedCurrencyId]
  );
  const selectedSalaryPeriodOption = useMemo(
    () =>
      SALARY_PERIOD_SELECT_OPTIONS.find((option) => option.value === salaryPeriod) ??
      SALARY_PERIOD_SELECT_OPTIONS[0],
    [salaryPeriod]
  );
  const selectedCurrencyOption = useMemo(
    () =>
      currencyOptions.find((option) => option.value === selectedCurrencyId) ??
      currencyOptions[0],
    [currencyOptions, selectedCurrencyId]
  );

  const debouncedSearch = useDebouncedValue(searchTerm, 350);
  const debouncedRange = useDebouncedValue(range, 350);

  /** Always send full sidebar state so parent merge does not keep stale API params when a field returns to its default (e.g. Week → All). */
  const changedFilters = useMemo<Filters>(
    () => ({
      relevant,
      locations,
      jobType,
      experience,
      salaryRange: debouncedRange,
      currency: Number.isFinite(Number.parseInt(selectedCurrencyId, 10))
        ? Number.parseInt(selectedCurrencyId, 10)
        : DEFAULT_CURRENCY_USD_ID,
      salaryTerm: salaryPeriod === "Hourly" ? "per_hour" : "annual",
      searchLocation: debouncedSearch.trim(),
    }),
    [
      relevant,
      locations,
      jobType,
      experience,
      debouncedRange,
      selectedCurrencyId,
      salaryPeriod,
      debouncedSearch,
    ]
  );

  useEffect(() => {
    if (initial.current) {
      initial.current = false;
      return;
    }
    onFilterChange(changedFilters);
  }, [changedFilters, onFilterChange]);

  useEffect(() => {
    searchRef.current?.focus();
    setTimeout(() => setLoading(false), 600);
  }, []);

  useEffect(() => {
    setRange((prev) => [
      Math.min(prev[0], currentSalarySliderMax),
      Math.min(prev[1], currentSalarySliderMax),
    ]);
  }, [currentSalarySliderMax]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await currenciesServices.getCurrencies();
        if (res.flag) {
          const list = "data" in res ? res.data || [] : [];
          const apiOptions: CurrencyOption[] = list.map((c: any) => ({
            value: String(c.id),
            label: `${c.code}`,
            symbol: c.symbol || USD_SALARY_SYMBOL,
            code: c.code,
          }));
          const allOption: CurrencyOption = {
            value: String(ALL_CURRENCIES_ID),
            label: "All currencies",
            symbol: USD_SALARY_SYMBOL,
            code: "ALL",
          };
          setCurrencyOptions([allOption, ...apiOptions]);
          const usd = apiOptions.find((o) => o.code === "USD");
          setSelectedCurrencyId((prev) => {
            if (
              usd &&
              prev === String(DEFAULT_CURRENCY_USD_ID) &&
              usd.value !== String(DEFAULT_CURRENCY_USD_ID)
            ) {
              return usd.value;
            }
            return prev;
          });
        } else {
          FlashMessage(res.message || "Unable to load currencies", "error");
        }
      } catch (e) {
        FlashMessage("Unable to load currencies", "error");
      }
    };

    fetchCurrencies();
  }, []);

  if (loading) return <JobSidebarLoader />;

  return (
    <>
      <Button
        className="filter-button"
        onClick={() => setFilterOpen(!filterOpen)}
      >
        Filters
      </Button>

      <Box className={`job-sidebar ${filterOpen ? "filter-open" : ""}`}>
        <FilterGroup
          title="Posted Time"
          mode="single"
          options={RELEVANT_OPTIONS}
          selected={[relevant]}
          onChange={(v) => setRelevant(v[0] ?? "All")}
          formatOptionLabel={(option) => {
            if (option === "All") return "All time";
            if (option === "Week") return "Last 7 days";
            if (option === "Month") return "Last 30 days";
            return option;
          }}
        />

        <Box className="search-option">
          <Typography variant="h5">Job Location</Typography>
          <Input
            ref={searchRef}
            type="text"
            className="job-location-search"
            placeholder="Search city, state or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FilterGroup
            mode="single"
            columns={2}
            embedded
            options={LOCATION_OPTIONS}
            selected={locations}
            onChange={setLocations}
          />
        </Box>

        <FilterGroup
          title="Job Type"
          options={JOB_TYPE_OPTIONS}
          mode="single"
          selected={jobType}
          onChange={setJobType}
        />

        <FilterGroup
          title="Experience Level"
          options={EXPERIENCE_OPTIONS}
          selected={experience}
          mode="single"
          onChange={setExperience}
        />

        <Box className="search-option">
          <h5>Salary Range</h5>
          <div className="salary-field-row">
            <SelectNew
              externalLabel={{ label: "Salary Period" }}
              name="salaryPeriod"
              options={SALARY_PERIOD_SELECT_OPTIONS}
              value={selectedSalaryPeriodOption}
              className="select-box"
              isSearchable={false}
              onChange={(option: { label: string; value: string } | null) =>
                setSalaryPeriod(
                  (option?.value as SalaryPeriodOption) || "Yearly"
                )
              }
            />
          </div>

          <div className="salary-field-row">
            <SelectNew
              externalLabel={{ label: "Currency" }}
              name="salaryCurrency"
              options={currencyOptions}
              value={selectedCurrencyOption}
              isSearchable={false}
              onChange={(option: { label: string; value: string } | null) =>
                setSelectedCurrencyId(
                  option?.value ?? String(DEFAULT_CURRENCY_USD_ID)
                )
              }
            />
          </div>
          <Slider
            value={range}
            min={0}
            max={currentSalarySliderMax}
            step={currentSalarySliderStep}
            onChange={(_, val) =>
              Array.isArray(val) && setRange([val[0], val[1]])
            }
          />
          <Box className="salary-values">
            {[0, 1].map((i) => (
              <Box key={i} className="salary-value">
                <Typography component="span" variant="body2" className="symbol">
                  {selectedCurrency.symbol || USD_SALARY_SYMBOL}
                </Typography>
                <Box className="value-box">
                  <Typography component="span" variant="body2">
                    {salaryPeriod === "Hourly" ? range[i] : `${range[i]} K`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>


        </Box>
      </Box>
    </>
  );
};

export default React.memo(JobSidebar);
